import React, {useCallback, useEffect, useRef, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Line} from "react-chartjs-2";
import {AppContext} from "../../HomePage";
import {SchedulerMonitorService} from "../../service/SchedulerMonitorService";
import AdminPageHeader from "./AdminPageHeader";
import {lastRunMemory, maxMemoryGrowth, memoryChartData, runningJobsAt, workerMemory} from "./schedulerMemory";

// Heroku-Scheduler-style read-only monitor for the scheduler worker: one row per
// job (frequency, last run + outcome, next due) from the scheduler_job_status
// collection the worker maintains; the `_worker` heartbeat doc drives the
// liveness banner. Auto-refreshes while open.
const REFRESH_MS = 30 * 1000;
const HEARTBEAT_STALE_MS = 2 * 60 * 1000;  // snapshot job stamps the heartbeat every minute
const OVERDUE_GRACE_MS = 15 * 60 * 1000;

const SchedulerMonitor = () => {

    const {t, setError} = React.useContext(AppContext);
    const service = useRef(new SchedulerMonitorService()).current;

    const [jobs, setJobs] = useState([]);
    const [worker, setWorker] = useState(null);
    const [memorySamples, setMemorySamples] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorJob, setErrorJob] = useState(null);  // row whose traceback is shown in the dialog
    const [nameQuery, setNameQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [frequencyFilter, setFrequencyFilter] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        service.getStatus()
            .then(status => {
                setJobs(status.jobs || []);
                setWorker(status.worker || null);
                setMemorySamples(status.memory_samples || []);
            })
            .catch(error => { setError(error); setError(null); })
            .finally(() => setLoading(false));
    }, [service, setError]);

    useEffect(() => {
        load();
        const timer = setInterval(load, REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    const formatDate = (value) => value ? new Date(value).toLocaleString() : '—';

    const pad2 = (value) => String(value).padStart(2, '0');

    // Cron fields are defined in UTC on the worker; render them in the viewer's
    // local clock so the whole table reads in one timezone (Last Run / Next Due
    // already do). Uses the current offset — fine for TR, which has no DST; the
    // cell tooltip keeps the raw UTC spec.
    const localOffsetMinutes = -new Date().getTimezoneOffset();

    const utcCronTimeToLocal = (hourUtc, minuteUtc) => {
        const total = hourUtc * 60 + minuteUtc + localOffsetMinutes;
        const dayShift = Math.floor(total / 1440);
        const localTotal = ((total % 1440) + 1440) % 1440;
        return {hour: Math.floor(localTotal / 60), minute: localTotal % 60, dayShift: dayShift};
    };

    // "day_of_week='0', hour='1', minute='30'" -> Heroku-style prose in local
    // time. APScheduler weekday 0 is Monday (not Sunday); the weekday label
    // shifts when the UTC->local conversion crosses midnight. Returns null —
    // raw display — for any shape the worker does not actually use (ranges,
    // steps, specific dates).
    const formatCronFields = (body) => {
        const fields = {};
        const fieldRegex = /(\w+)='([^']*)'/g;
        let match;
        while ((match = fieldRegex.exec(body)) !== null) {
            fields[match[1]] = match[2];
        }
        const numberList = (value) => value === undefined || /^[0-9,]+$/.test(value);
        const singleNumber = (value) => value === undefined || /^[0-9]+$/.test(value);
        const unknownKeys = Object.keys(fields)
            .filter(key => !['day', 'day_of_week', 'hour', 'minute'].includes(key));
        if (unknownKeys.length || (fields.day !== undefined && fields.day !== '*')
            || !numberList(fields.day_of_week) || !numberList(fields.hour) || !numberList(fields.minute)) {
            return null;
        }
        if (fields.hour === undefined) {
            if (fields.minute === undefined) return null;
            const minutes = fields.minute.split(',').map(Number);
            if (minutes.length > 1) {
                // Staggered fixed-step list, e.g. '2,12,22,32,42,52' -> every 10 min.
                const step = minutes[1] - minutes[0];
                const uniform = step > 0 && minutes.length === Math.floor(60 / step)
                    && minutes.every((value, index) => value === minutes[0] + index * step);
                return uniform ? `${t('scheduler_monitor.every')} ${step} ${t('scheduler_monitor.min')}` : null;
            }
            const localMinute = ((minutes[0] + localOffsetMinutes) % 60 + 60) % 60;
            return t('scheduler_monitor.hourly_at', {minute: pad2(localMinute)});
        }
        if (!singleNumber(fields.minute)) return null;
        const minuteUtc = Number(fields.minute === undefined ? 0 : fields.minute);
        const localTimes = fields.hour.split(',').map(hour => utcCronTimeToLocal(Number(hour), minuteUtc));
        const times = localTimes
            .slice().sort((a, b) => (a.hour * 60 + a.minute) - (b.hour * 60 + b.minute))
            .map(item => `${pad2(item.hour)}:${pad2(item.minute)}`).join(', ');
        if (fields.day_of_week !== undefined) {
            // Weekly jobs here always have a single time; bail out to the raw
            // display rather than guess how a multi-time weekday would shift.
            if (localTimes.length !== 1) return null;
            const weekday = fields.day_of_week.split(',')
                .map(day => t('scheduler_monitor.weekday_' + ((Number(day) + localTimes[0].dayShift + 7) % 7)))
                .join(', ');
            return t('scheduler_monitor.weekly_at', {weekday: weekday, times: times});
        }
        return t('scheduler_monitor.daily_at', {times: times});
    };

    // str(apscheduler trigger): "interval[0:10:00]" / "cron[hour='0,6,12,18', minute='0']"
    const formatTrigger = (trigger) => {
        if (!trigger) return '—';
        const interval = trigger.match(/^interval\[(\d+):(\d{2}):(\d{2})\]$/);
        if (interval) {
            const totalMinutes = Number(interval[1]) * 60 + Number(interval[2]);
            if (totalMinutes === 0) {
                return `${t('scheduler_monitor.every')} ${Number(interval[3])} ${t('scheduler_monitor.sec')}`;
            }
            return `${t('scheduler_monitor.every')} ${totalMinutes} ${t('scheduler_monitor.min')}`;
        }
        const cron = trigger.match(/^cron\[(.+)\]$/);
        if (cron) {
            const readable = formatCronFields(cron[1]);
            return readable || `${t('scheduler_monitor.cron')}: ${cron[1].replace(/'/g, '')}`;
        }
        return trigger;
    };

    // One-line "what does this job do" subtitle; unknown job names simply get
    // no subtitle (i18next returns the key itself when a translation is missing).
    const jobDescription = (jobName) => {
        const key = 'scheduler_monitor.job_desc.' + jobName;
        const text = t(key);
        return text === key ? null : text;
    };

    const jobBody = (row) => {
        const description = jobDescription(row.job_name);
        return (
            <div>
                <span style={{fontFamily: 'monospace'}}>{row.job_name}</span>
                {description &&
                    <div style={{fontSize: '0.8rem', color: 'var(--text-color-secondary, #6c757d)', marginTop: '2px'}}>
                        {description}
                    </div>}
            </div>
        );
    };

    const durationBody = (row) => {
        const seconds = row.last_duration_seconds;
        if (seconds === null || seconds === undefined) return '—';
        return seconds < 60 ? `${seconds.toFixed(1)} ${t('scheduler_monitor.sec')}`
            : `${(seconds / 60).toFixed(1)} ${t('scheduler_monitor.min')}`;
    };

    const statusBody = (row) => {
        if (!row.last_status) return <Tag value={t('scheduler_monitor.never_ran')}/>;
        const severity = row.last_status === 'success' ? 'success'
            : row.last_status === 'failed' ? 'danger'
                : row.last_status === 'interrupted' ? 'warning' : 'info';
        return (
            <div>
                <Tag value={t('scheduler_monitor.' + row.last_status)} severity={severity}/>
                {row.last_status === 'failed' && row.last_error &&
                    <Button icon="pi pi-search" className="p-button-text p-button-danger p-button-sm"
                            style={{marginLeft: '0.3rem'}} title={t('scheduler_monitor.last_error')}
                            onClick={() => setErrorJob(row)}/>}
                {row.last_result &&
                    <div style={{fontSize: '0.8rem', color: 'var(--text-color-secondary, #6c757d)', marginTop: '2px'}}>
                        {row.last_result}
                    </div>}
            </div>
        );
    };

    const memoryLabels = {rss: t('scheduler_monitor.rss'), container: t('scheduler_monitor.container'),
                          container_limit: t('scheduler_monitor.container_limit')};

    // Last run's RSS start → end (growth), and the largest growth any run of
    // this job showed. Jobs share one process, so a job running beside a heavy
    // one inherits part of its growth; the one that keeps growing is the suspect.
    const memoryBody = (row) => {
        const lastRun = lastRunMemory(row);
        if (!lastRun) return '—';
        const maxGrowth = maxMemoryGrowth(row);
        return (
            <div>
                <span style={{whiteSpace: 'nowrap'}}>{lastRun}</span>
                {maxGrowth &&
                    <div style={{fontSize: '0.8rem', color: 'var(--text-color-secondary, #6c757d)', marginTop: '2px'}}>
                        {t('scheduler_monitor.max_growth')}: {maxGrowth} MB
                    </div>}
            </div>
        );
    };

    const memoryChartOptions = {
        maintainAspectRatio: false,
        animation: false,
        interaction: {mode: 'index', intersect: false},
        scales: {y: {beginAtZero: true, title: {display: true, text: 'MB'}},
                 x: {ticks: {maxTicksLimit: 12}}},
        plugins: {
            tooltip: {
                callbacks: {
                    // The jobs running at that minute: what the memory climbed under.
                    footer: (items) => {
                        const jobs = items.length ? runningJobsAt(memorySamples, items[0].dataIndex) : [];
                        return jobs.length ? `${t('scheduler_monitor.running_now')}: ${jobs.join(', ')}` : '';
                    },
                },
            },
        },
    };

    const heartbeatAgeMs = worker && worker.heartbeat_at
        ? Date.now() - new Date(worker.heartbeat_at).getTime() : null;
    const workerAlive = heartbeatAgeMs !== null && heartbeatAgeMs < HEARTBEAT_STALE_MS;

    const nextDueBody = (row) => {
        // A doc missing from the latest snapshot belongs to a job the scheduler
        // no longer runs (e.g. removed after a deploy) — flag instead of a stale time.
        const snapshotLagMs = worker && worker.heartbeat_at && row.snapshot_at
            ? new Date(worker.heartbeat_at).getTime() - new Date(row.snapshot_at).getTime() : null;
        if (snapshotLagMs !== null && snapshotLagMs > HEARTBEAT_STALE_MS) {
            return <Tag value={t('scheduler_monitor.not_scheduled')} severity="warning"/>;
        }
        if (!row.next_run_time) return '—';
        const overdue = workerAlive
            && (Date.now() - new Date(row.next_run_time).getTime()) > OVERDUE_GRACE_MS;
        return (
            <span>
                {formatDate(row.next_run_time)}
                {overdue &&
                    <Tag value={t('scheduler_monitor.overdue')} severity="danger" style={{marginLeft: '0.3rem'}}/>}
            </span>
        );
    };

    // Every whitespace-separated token must appear in the job name or its
    // description (case-insensitive contains), so "competitor daily" narrows
    // to the daily competitor jobs.
    const matchesNameQuery = (row) => {
        const tokens = nameQuery.toLowerCase().split(/\s+/).filter(Boolean);
        if (!tokens.length) return true;
        const haystack = (row.job_name + ' ' + (jobDescription(row.job_name) || '')).toLowerCase();
        return tokens.every(token => haystack.includes(token));
    };

    const filteredJobs = jobs.filter(row =>
        (statusFilter === null
            || (statusFilter === 'never_ran' ? !row.last_status : row.last_status === statusFilter))
        && (frequencyFilter === null || formatTrigger(row.trigger) === frequencyFilter)
        && matchesNameQuery(row));

    const statusOptions = ['success', 'failed', 'running', 'interrupted', 'never_ran']
        .map(value => ({value: value, label: t('scheduler_monitor.' + value)}));
    const frequencyOptions = [...new Set(jobs.map(row => formatTrigger(row.trigger)))]
        .sort((a, b) => a.localeCompare(b))
        .map(value => ({value: value, label: value}));

    const workerBanner = () => {
        if (!worker) return <Tag value={t('scheduler_monitor.worker_down')} severity="danger"/>;
        if (workerAlive) {
            const seconds = Math.round(heartbeatAgeMs / 1000);
            const host = worker.host ? ` · ${worker.host}` : '';
            const memory = workerMemory(worker, memoryLabels);
            return <Tag severity="success"
                        value={`${t('scheduler_monitor.worker_alive')} · ${t('scheduler_monitor.last_heartbeat')}: ` +
                            `${seconds} ${t('scheduler_monitor.sec')} · ${worker.job_count} ${t('scheduler_monitor.job_count')}${host}` +
                            (memory ? ` · ${memory}` : '')}/>;
        }
        return <Tag severity="danger"
                    value={`${t('scheduler_monitor.worker_down')} · ${t('scheduler_monitor.last_heartbeat')}: ` +
                        formatDate(worker.heartbeat_at)}/>;
    };

    return (
        <div className="card management-page">
            <AdminPageHeader
                title={t('menu_item.scheduler_monitor')}
                subtitle={t('menu.management_hub.page.scheduler_monitor')}
                actions={<Button icon="pi pi-refresh" label={t('scheduler_monitor.refresh')}
                                 className="p-button-sm" onClick={load}/>}
            />
            <div style={{marginBottom: '1rem'}}>
                {workerBanner()}
                <small style={{marginLeft: '0.75rem'}}>{t('scheduler_monitor.auto_refresh')}</small>
            </div>
            {memorySamples.length > 0 &&
                <div style={{marginBottom: '1rem'}}>
                    <h5 style={{margin: '0 0 0.5rem'}}>{t('scheduler_monitor.memory_chart')}</h5>
                    <div style={{height: '220px'}}>
                        <Line data={memoryChartData(memorySamples, memoryLabels)} options={memoryChartOptions}/>
                    </div>
                </div>}
            <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center',
                         marginBottom: '1rem'}}>
                <InputText value={nameQuery} onChange={(e) => setNameQuery(e.target.value)}
                           placeholder={t('scheduler_monitor.search_placeholder')}
                           style={{flex: '1 1 220px'}}/>
                <Dropdown value={statusFilter} options={statusOptions} showClear={true}
                          onChange={(e) => setStatusFilter(e.value === undefined ? null : e.value)}
                          placeholder={t('scheduler_monitor.all_statuses')} style={{width: '180px'}}/>
                <Dropdown value={frequencyFilter} options={frequencyOptions} showClear={true}
                          onChange={(e) => setFrequencyFilter(e.value === undefined ? null : e.value)}
                          placeholder={t('scheduler_monitor.all_frequencies')} style={{width: '220px'}}/>
                <small>{filteredJobs.length} / {jobs.length}</small>
            </div>
            <DataTable value={filteredJobs} loading={loading} dataKey="job_name"
                       emptyMessage={t('scheduler_monitor.no_jobs')}>
                <Column field="job_name" header={t('scheduler_monitor.job')} sortable={true}
                        body={jobBody}/>
                <Column field="trigger" header={t('scheduler_monitor.frequency')}
                        body={(row) => <span title={row.trigger}>{formatTrigger(row.trigger)}</span>}/>
                <Column field="last_started_at" header={t('scheduler_monitor.last_run')} sortable={true}
                        body={(row) => formatDate(row.last_started_at)}/>
                <Column field="last_duration_seconds" header={t('scheduler_monitor.duration')} sortable={true}
                        body={durationBody}/>
                <Column field="last_status" header={t('scheduler_monitor.status')} sortable={true}
                        body={statusBody}/>
                <Column field="max_rss_delta_mb" header={t('scheduler_monitor.memory')} sortable={true}
                        body={memoryBody}/>
                <Column field="next_run_time" header={t('scheduler_monitor.next_due')} sortable={true}
                        body={nextDueBody}/>
            </DataTable>
            <Dialog header={errorJob ? errorJob.job_name : ''} visible={!!errorJob}
                    style={{width: '60vw'}} onHide={() => setErrorJob(null)}>
                <pre style={{whiteSpace: 'pre-wrap', fontSize: '0.85rem'}}>
                    {errorJob && errorJob.last_error}
                </pre>
            </Dialog>
        </div>
    );
};

export default SchedulerMonitor;
